import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {IconName} from '../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {boxShadow} from '../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {settingsNavigationWidth} from '../settingsCommonConstants';
import {SettingsHomeShortcutIcon} from './settingsHomeShortcutIcon';

/*
 * Props.
 */

interface SettingsHomeShortcutProps {
  onPrimaryClick: Handler;
  primaryLabel: ReactNode;
  onSecondaryClick?: Handler;
  secondaryLabel?: ReactNode;
  title: ReactNode;
  description: ReactNode;
  icon: IconName;
  noMarginBottom?: boolean;
  columnSpan?: number;
}

/*
 * Style.
 */

type ShortcutDivStyleProps = {
  $noMarginBottom?: boolean;
  $columnSpan?: number;
};
const StyledShortcutDiv = styled.div<ShortcutDivStyleProps>`
  grid-column-end: span 1;

  @media (min-width: ${1200 + settingsNavigationWidth}px) {
    grid-column-end: ${(p) => (p.$columnSpan ? `span ${p.$columnSpan}` : 'span 1')};
  }

  border-radius: 16px;
  background: ${(p) => p.theme.greys.white};
  ${(p) => boxShadow(p.theme, `0 1px 9px ${p.theme.alphas.black20}`)};
  border: 1px solid ${(p) => p.theme.alphas.black20};
  padding: 19px;

  display: grid;
  grid-template-areas: 'icon . content';
  grid-template-columns: auto 16px 1fr;

  min-width: 300px;
  margin: 0;
  overflow: hidden;

  width: 100%;
`;

const StyledShortcutTitle = styled.h3`
  margin: 0;
  font-size: ${fontSizes.large};
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.bold};
`;

const StyledShortcutParagraph = styled.p`
  font-size: ${fontSizes.medium};
  color: ${(p) => p.theme.greys.shade70};
  font-weight: ${fontWeights.normal};
  line-height: 1.4;
  margin-top: 7px;
`;

const StyledContentWrapper = styled.div`
  grid-area: content;
  padding-top: 12px;

  display: flex;
  flex-direction: column;
`;

const StyledTextWrapper = styled.div`
  flex-grow: 1;
`;

const StyledShortcutActions = styled.div`
  display: flex;
  justify-content: flex-end;

  margin-top: 31px;

  & > div {
    margin-right: 6px;
  }
`;

/*
 * Component.
 */

export const SettingsHomeShortcut: FC<SettingsHomeShortcutProps> = (props) => (
  <StyledShortcutDiv $noMarginBottom={props.noMarginBottom} $columnSpan={props.columnSpan}>
    <SettingsHomeShortcutIcon icon={props.icon} />
    <StyledContentWrapper>
      <StyledTextWrapper>
        <StyledShortcutTitle>{props.title}</StyledShortcutTitle>
        <StyledShortcutParagraph>{props.description}</StyledShortcutParagraph>
      </StyledTextWrapper>
      <StyledShortcutActions>
        {maybeRenderSecondaryButton(props)}
        <Button variant={ButtonVariantsEnum.PRIMARY} isRounded onClick={props.onPrimaryClick}>
          {props.primaryLabel}
        </Button>
      </StyledShortcutActions>
    </StyledContentWrapper>
  </StyledShortcutDiv>
);

function maybeRenderSecondaryButton(props: SettingsHomeShortcutProps) {
  if (!props.onSecondaryClick || !props.secondaryLabel) {
    return null;
  }

  return (
    <Button variant={ButtonVariantsEnum.LINK} isRounded onClick={props.onSecondaryClick}>
      {props.secondaryLabel}
    </Button>
  );
}
